import type {
  ArticlesMessage,
  AttachmentConversationMessage,
  Conversation,
  ConversationArticle,
  ConversationLogItemMessageAdded,
  ConversationParticipant,
  InlineDataConversationMessage,
  OptionsMessage,
  RichTextConversationMessage,
  TextConversationMessage,
} from '@stripe-internal/conversations-client';

/**
 * Important: These helpers are entirely duplicated from the buildTranscript.ts in conversations-assistant.
 * We can't share them with the current setup, but we should refactor to not need to duplicate.
 */

export type RequestIssueContext = {
  icTagsEncountered: string[];
  articlesVisited: string[];
  transcript?: string;
};

function buildTextTranscriptLine(
  participant: ConversationParticipant,
  message: TextConversationMessage,
): string {
  return `${participant.displayName}: ${message.text}\n`;
}

function buildAttachmentTranscriptLine(
  participant: ConversationParticipant,
  message: AttachmentConversationMessage,
): string {
  const attachmentName =
    message.attachment.name || message.attachment.file?.name;
  return `${participant.displayName}: Added attachment ${attachmentName}\n`;
}

function buildOptionsTranscriptLine(
  message: OptionsMessage,
  participant: ConversationParticipant,
): string {
  const option = message.options.find((o) => o.id === message.selectedOptionId);
  let line = `${participant.displayName}: `;
  if (option) {
    line += `Selected option ${option.label}`;
  } else {
    line += `Was presented options ${message.options
      .map((o) => o.label)
      .join(', ')}`;
  }
  if (message.purpose) {
    line += ` for purpose ${message.purpose}`;
  }
  return `${line}\n`;
}

function buildArticlesTranscriptLine(
  conversation: Conversation,
  participant: ConversationParticipant,
  message: ArticlesMessage,
): string {
  const uhContext = conversation.context.universalHelp as
    | {
        articlesVisited?: Record<string, ConversationArticle>;
      }
    | undefined;
  const articlesVisited = uhContext?.articlesVisited;
  return `${participant.displayName}: Was presented articles: ${message.articles
    .map((article) => {
      const visited = articlesVisited && articlesVisited[article.id];
      return `${article.url}${visited ? ' (visited)' : ''}`;
    })
    .join(', ')}\n`;
}

function buildInlineDataTranscriptLine(
  participant: ConversationParticipant,
  message: InlineDataConversationMessage,
): string {
  let inlineDataTranscript = '';
  const inlineDataName = message.inlineData.name;
  const inlineDataValue = Array.isArray(message.inlineData.data)
    ? (message.inlineData.data[0]?.value as object | undefined)
    : undefined;
  if (!inlineDataValue) {
    return `${participant.displayName}: * InlineData ${inlineDataName} without value. *\n`;
  }

  switch (inlineDataName) {
    case 'universal_help_sign_in_prompt':
      inlineDataTranscript = 'User prompted to sign in. ';
      if ('merchantName' in inlineDataValue && inlineDataValue.merchantName) {
        inlineDataTranscript += `User signed in as ${inlineDataValue.merchantName}.`;
      } else {
        inlineDataTranscript += 'User could not sign in.';
      }
      break;
    case 'email_verification':
      inlineDataTranscript = 'User prompted to verify their email. ';
      if ('selected_option_id' in inlineDataValue) {
        if (inlineDataValue.selected_option_id === 'noaccess') {
          if (
            'email_send_count' in inlineDataValue &&
            (inlineDataValue.email_send_count as number) > 1
          ) {
            inlineDataTranscript +=
              "User didn't recieve the verification email.";
          } else {
            inlineDataTranscript += "User doesn't have access to the email.";
          }
        } else if (inlineDataValue.selected_option_id === 'resend') {
          inlineDataTranscript +=
            'User requested to resend email verification.';
        }
      }
      break;
    case 'merchant_related_case':
      inlineDataTranscript = 'User shown related case.';
      if ('related_case' in inlineDataValue) {
        inlineDataTranscript += `Case: ${inlineDataValue.related_case}.`;
      }
      break;
    default:
      inlineDataTranscript = `Unsuported transcript for inline data ${inlineDataName}.`;
  }
  return `${participant.displayName}: ${inlineDataTranscript}\n`;
}

function buildRichTextTranscriptLine(
  conversation: Conversation,
  participant: ConversationParticipant,
  message: RichTextConversationMessage,
): string {
  let line = `${participant.displayName}: ${message.text}\n\n`;

  switch (message.data.type) {
    case 'button-options-conversation-message':
    case 'select-options-conversation-message':
      line += buildOptionsTranscriptLine(message.data, participant);
      break;
    case 'articles-conversation-message':
      line += buildArticlesTranscriptLine(
        conversation,
        participant,
        message.data,
      );
      break;
    default:
  }

  return line;
}

function buildTranscript(conversation: Conversation): string {
  const lines: string[] = [];

  conversation.log
    .get()
    .filter((item) => item.name === 'conversation-message-added')
    .forEach((item) => {
      const {message} = item as ConversationLogItemMessageAdded;
      const {participant} = message;

      switch (message.type) {
        case 'text-conversation-message':
          lines.push(buildTextTranscriptLine(participant, message));
          break;
        case 'attachment-conversation-message':
          lines.push(buildAttachmentTranscriptLine(participant, message));
          break;
        case 'button-options-conversation-message':
        case 'select-options-conversation-message':
          lines.push(buildOptionsTranscriptLine(message, participant));
          break;
        case 'articles-conversation-message':
          lines.push(
            buildArticlesTranscriptLine(conversation, participant, message),
          );
          break;
        case 'rich-text-conversation-message':
          lines.push(
            buildRichTextTranscriptLine(conversation, participant, message),
          );
          break;
        case 'inline-data-conversation-message':
          lines.push(buildInlineDataTranscriptLine(participant, message));
          break;
        default:
      }
    });

  return lines.join('\n');
}

export function buildUHConversationIssueContext(
  conversation: Conversation,
): RequestIssueContext {
  const uhContext = conversation.context.universalHelp as
    | {
        articlesVisited?: Record<string, ConversationArticle>;
      }
    | undefined;

  return {
    articlesVisited: uhContext?.articlesVisited
      ? Object.keys(uhContext.articlesVisited)
      : [],
    icTagsEncountered: [],
    transcript: buildTranscript(conversation),
  };
}
